import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {saveStatusUrl, loadStatusUrl} from 'llm/deep-research/DeepResearchChatStorage';
import {
  ProgressDataResponse,
  SendDeepResearchMessageResponse,
  getUserMessage,
  initiateDeepResearchMessage,
  pollDeepResearchMessage,
} from 'llm/deep-research/DeepResearchDAO';
import {
  DeepResearchMessageComponent,
  DeepResearchMessage,
} from 'llm/deep-research/messages/DeepResearchMessage';
import {
  ProgressDeepResearchMessage,
  ProgressData,
  ProgressStatus,
} from 'llm/deep-research/messages/DeepResearchProgress';
import {ReportDeepResearchMessage} from 'llm/deep-research/messages/DeepResearchReport';
import {DeepResearchMessageType} from 'llm/deep-research/types';
import {convertProgressDataResponseToProgressData} from 'llm/deep-research/utils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

export interface DeepResearchMessageThreadControls {
  messages: Array<DeepResearchMessage>;
  progressData: ProgressData | null;
  chatId: string | undefined;
  isSubmitting: boolean;
  isDisabled: boolean;
  sendMessage: (messageContent: string, publicDataEnabled: boolean) => Promise<void>;
  clearChat: () => void;
}

export function useDeepResearchMessageThread({
  chatId,
  onCreateChat,
  onRequestNewChat,
}: {
  chatId: string | null;
  onCreateChat: (chatId: string) => void;
  onRequestNewChat: () => void;
}): DeepResearchMessageThreadControls {
  // Create a ref to store the AbortController
  const initialStatusUrl = loadStatusUrl(chatId);

  const [messages, setMessages] = useState<Array<DeepResearchMessage>>([]);
  const [statusUrl, setStatusUrl] = useState<string | undefined>(initialStatusUrl);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);

  // isDisabled controls whether the chat input is disabled. This is a temp variable to disable multi-turn
  // chats
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  interface UpdateProgressData {
    newProgressData?: ProgressData;
    status?: ProgressStatus;
    errorMessage?: string | null;
  }

  const updateProgressData = useCallback(({newProgressData, status, errorMessage}: UpdateProgressData) => {
    // Update progress data
    if (newProgressData) {
      setProgressData(newProgressData);
    }

    if (status) {
      // Update or add progress message
      setMessages((prevMessages) => {
        const progressMessageIndex = prevMessages.findLastIndex(
          (msg) => msg.type === DeepResearchMessageType.PROGRESS
        );

        const updatedMessages = [...prevMessages];
        const progressMessage: ProgressDeepResearchMessage = {
          type: DeepResearchMessageType.PROGRESS,
          status,
          errorMessage: errorMessage || undefined,
        };

        if (progressMessageIndex === -1) {
          // Add new progress message
          updatedMessages.push(progressMessage);
        } else {
          // Update existing progress message
          updatedMessages[progressMessageIndex] = progressMessage;
        }
        return updatedMessages;
      });
    }
  }, []);

  const addSystemReport = useCallback((content: string) => {
    const systemMessage: ReportDeepResearchMessage = {
      type: DeepResearchMessageType.SYSTEM_REPORT,
      content,
    };
    setMessages((prevMessages) => [...prevMessages, systemMessage]);
  }, []);

  // Poll for the result and set the messages
  useEffect(() => {
    const abortController = new AbortController();
    if (chatId) {
      setIsDisabled(true);
      setIsSubmitting(true);

      // Now poll for the result
      pollDeepResearchMessage(
        chatId,
        statusUrl ?? null,
        abortController.signal,
        (progressDataResponse: ProgressDataResponse, result: SendDeepResearchMessageResponse | null) => {
          // Replace the user message in the chat with the one
          const userMessageContent = getUserMessage(progressDataResponse);
          if (userMessageContent) {
            setMessages((prevMessages: Array<DeepResearchMessage>) => {
              let userMessageIndex = prevMessages.findIndex(
                (m: DeepResearchMessage) => m.type === DeepResearchMessageType.USER_MESSAGE
              );
              if (userMessageIndex === -1) {
                userMessageIndex = prevMessages.length;
              }
              const updatedMessages = [...prevMessages];
              updatedMessages[userMessageIndex] = {
                type: DeepResearchMessageType.USER_MESSAGE,
                content: userMessageContent,
              };
              return updatedMessages;
            });
          }

          if (result) {
            switch (result.type) {
              case 'completed':
                updateProgressData({
                  newProgressData: convertProgressDataResponseToProgressData(progressDataResponse),
                  status: ProgressStatus.COMPLETED,
                });
                addSystemReport(result.response);
                break;
              case 'failed':
                updateProgressData({
                  newProgressData: convertProgressDataResponseToProgressData(progressDataResponse),
                  status: ProgressStatus.FAILED,
                  errorMessage: result.errorMessage,
                });
                break;
              default:
                throw new Error(`Unknown result type: ${assertNever(result).type}`);
            }
            setIsSubmitting(false);
          } else {
            updateProgressData({
              newProgressData: convertProgressDataResponseToProgressData(progressDataResponse),
              status: ProgressStatus.IN_PROGRESS,
              errorMessage:
                !statusUrl &&
                new Date(progressDataResponse.chatMetadata.createdAt) <
                  new Date(Date.now() - 1 * 60 * 60 * 1000) // 1 hour
                  ? 'Deep research is taking longer than expected.'
                  : null,
            });
          }
        }
      ).catch((error) => {
        // If the error is from an abort, we can safely ignore it
        if (error?.name !== 'AbortError') {
          throw error;
        }
      });
    } else {
      setMessages([]);
      setProgressData(null);
      setIsDisabled(false);
      setIsSubmitting(false);
    }
    return () => {
      abortController.abort();
    };
  }, [chatId, statusUrl, addSystemReport, updateProgressData]);

  const sendMessage = useCallback(
    async (messageContent: string, publicDataEnabled: boolean) => {
      const trimmedMessage = messageContent.trim();

      const userMessage: DeepResearchMessage = {
        type: DeepResearchMessageType.USER_MESSAGE,
        content: trimmedMessage,
      };

      setMessages((prevMessages) => [...prevMessages, userMessage]);
      updateProgressData({
        newProgressData: {numSources: 0, historyThread: []},
        status: ProgressStatus.STARTED,
      });
      setIsSubmitting(true);
      setIsDisabled(true);

      // First initiate the request to get the chatId immediately
      // TODO (BNCH-126624): Add try/catch for error handling
      const initResponse = await initiateDeepResearchMessage(
        chatId ?? undefined,
        trimmedMessage,
        publicDataEnabled
      );

      // Store chatId as soon as we get it
      if (chatId && chatId !== initResponse.chatId) {
        logging.error('[llm] Chat ID mismatch', {
          extra: {storedChatId: chatId, responseChatId: initResponse.chatId},
        });
      }

      saveStatusUrl(initResponse.chatId, initResponse.statusUrl);

      // Set chat ID after all storage is updated
      onCreateChat(initResponse.chatId);
      setStatusUrl(initResponse.statusUrl);
    },
    [chatId, setIsSubmitting, setIsDisabled, onCreateChat, updateProgressData]
  );

  const clearChat = useCallback(() => {
    // Reset states to allow new input
    onRequestNewChat();
    setStatusUrl(undefined);
  }, [onRequestNewChat]);

  return {
    messages,
    progressData,
    chatId: chatId || undefined,
    isSubmitting,
    isDisabled,
    sendMessage,
    clearChat,
  };
}

export interface DeepResearchMessageThreadProps {
  messages: Array<DeepResearchMessage>;
  progressData: ProgressData | null;
  openActivityModal: (activityData: ProgressData, selectedToolId?: string) => void;
}

export function DeepResearchMessageThread({
  messages,
  progressData,
  openActivityModal,
}: DeepResearchMessageThreadProps): JSX.Element {
  return (
    <div className={css(styles.threadContainer)}>
      {messages.length === 0 && <CenteredHorizontalThrobber />}
      {messages.map((message, index) => (
        <DeepResearchMessageComponent
          key={index}
          message={message}
          progressData={progressData}
          openActivityModal={openActivityModal}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: '12px',
    height: '100%',
    width: '100%',
  },
});
